import { useQuery } from '@tanstack/react-query';
import { BookOpen, Check, Eye, Pencil, Trash2 } from 'lucide-react';
import { api } from '../lib/api.ts';
import { useAuth } from '../contexto/auth.tsx';
import type { Nivel } from '../contexto/auth.tsx';
import { Aviso, Carregando } from '../componentes/ui.tsx';

interface Bloco {
  titulo?: string;
  texto: string;
}

interface Secao {
  id: string;
  titulo: string;
  resumo: string;
  modulos: string[];
  blocos: Bloco[];
  acoes?: { nivel: Nivel; itens: string[] }[];
}

const ROTULO_DO_NIVEL: Record<Nivel, string> = {
  visualizar: 'Visualização',
  editar: 'Edição',
  excluir: 'Exclusão',
};

const ICONE_DO_NIVEL: Record<Nivel, typeof Eye> = {
  visualizar: Eye,
  editar: Pencil,
  excluir: Trash2,
};

/**
 * A explicação do sistema.
 *
 * O conteúdo vem do servidor já recortado pelo que a pessoa pode ver: esta
 * tela não recebe o texto inteiro para depois esconder pedaços. Se um
 * administrador liberar um acesso, a seção correspondente aparece na próxima
 * vez que a página for aberta.
 */
export function Ajuda() {
  const { usuario, ehAdmin } = useAuth();

  const consulta = useQuery({
    queryKey: ['ajuda'],
    queryFn: () => api<{ perfil: string; secoes: Secao[] }>('/ajuda'),
  });

  if (consulta.isLoading) return <Carregando texto="Carregando a explicação…" />;
  if (consulta.isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar a explicação">
        <p>{(consulta.error as Error).message}</p>
      </Aviso>
    );
  }

  const secoes = consulta.data?.secoes ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <BookOpen className="size-6 text-slate-400" />
          Como funciona
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">
          {ehAdmin
            ? 'Você é administrador, então está vendo a explicação completa do sistema.'
            : 'Você está vendo as partes que o seu perfil tem permissão de visualizar. Se um administrador liberar um acesso novo, a explicação correspondente passa a aparecer aqui.'}
        </p>
      </div>

      {/* Um índice, porque a página é longa e quase ninguém lê do começo. */}
      <nav className="flex flex-wrap gap-2">
        {secoes.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-600
                       transition hover:border-slate-400 hover:text-slate-900"
          >
            {s.titulo}
          </a>
        ))}
      </nav>

      <div className="space-y-4">
        {secoes.map((s) => (
          <section
            key={s.id}
            id={s.id}
            className="scroll-mt-6 rounded border border-slate-200 bg-white p-5"
          >
            <h2 className="text-lg font-semibold text-slate-900">{s.titulo}</h2>
            <p className="mt-0.5 text-sm text-slate-600">{s.resumo}</p>

            <div className="mt-4 space-y-4">
              {s.blocos.map((b, i) => (
                <div key={i}>
                  {b.titulo !== undefined && (
                    <p className="mb-1 text-xs font-medium tracking-wide text-slate-500 uppercase">
                      {b.titulo}
                    </p>
                  )}
                  <p className="max-w-prose text-sm leading-relaxed text-slate-700">{b.texto}</p>
                </div>
              ))}
            </div>

            {s.acoes !== undefined && s.acoes.length > 0 && (
              <div className="mt-5 rounded bg-slate-50 p-4">
                <p className="mb-3 text-xs font-medium tracking-wide text-slate-500 uppercase">
                  O que você pode fazer aqui
                </p>
                <div className="space-y-3">
                  {s.acoes.map((a) => {
                    const Icone = ICONE_DO_NIVEL[a.nivel];
                    return (
                      <div key={a.nivel}>
                        <p className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                          <Icone className="size-3.5 text-slate-400" />
                          {ROTULO_DO_NIVEL[a.nivel]}
                        </p>
                        <ul className="space-y-0.5">
                          {a.itens.map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-1.5 text-sm text-slate-600"
                            >
                              <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        ))}
      </div>

      <p className="text-xs text-slate-500">
        Seu perfil: <span className="font-medium text-slate-700">{usuario?.perfil}</span> ·{' '}
        {secoes.length} {secoes.length === 1 ? 'assunto' : 'assuntos'} liberados para você.
      </p>
    </div>
  );
}
